import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Stack, TextField, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Swal from "sweetalert2";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { defaultSwalPreferences } from "app/utils/appHelpers";
import { isValidPhone } from "app/utils/phone";
import { PHONE_BLOCKING_STATUSES, PhoneField, SaveButton, StandardPageHeader } from "app/shared/ui";
import accountServices from "app/services/account-services";
import ApiAccessSection from "./ApiAccessSection";
import WhatsappVerificationDialog from "./WhatsappVerificationDialog";

const MIN_PASSWORD_LENGTH = 6;
const EMPTY_PASSWORDS = { current_password: "", password: "", password_confirmation: "" };

const notify = (title) => {
    Swal.mixin(defaultSwalPreferences(Swal)).fire({ icon: "success", title, background: "success" });
};

const SectionCard = ({ title, subtitle, children }) => (
    <Card variant="outlined" sx={{ borderRadius: 2.5, height: "100%" }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h4" color="primary">{title}</Typography>
            {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>}
            {children}
        </CardContent>
    </Card>
);

// Cualquier perfil (también lector y supervisor) cambia aquí su nombre, su teléfono y su contraseña, y verifica su
// teléfono con un código por WhatsApp (Xpit Assistant por WhatsApp solo atiende números verificados). Al guardar un
// teléfono nuevo, el código se envía solo. El correo, los perfiles y las empresas los cambia un administrador desde Usuarios.
const MyProfile = () => {
    const { authUser, setAuthData } = useJumboAuth();
    const [saved, setSaved] = useState(null);
    const [account, setAccount] = useState(null);
    const [phoneStatus, setPhoneStatus] = useState("idle");
    const [saving, setSaving] = useState(false);
    const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
    const [changingPassword, setChangingPassword] = useState(false);
    const [verifyingPhone, setVerifyingPhone] = useState(false);

    useEffect(() => {
        accountServices.getAccount().then((data) => {
            setSaved(data);
            setAccount(data);
        }).catch(() => {});
    }, []);

    const changed = !!account && ["first_name", "last_name", "phone_primary"].some((field) => (account[field] || "") !== (saved[field] || ""));
    const phoneChanged = !!account && (account.phone_primary || "") !== (saved.phone_primary || "");
    const badPhone = !!account?.phone_primary && (!isValidPhone(account.phone_primary) || PHONE_BLOCKING_STATUSES.includes(phoneStatus));
    const canSave = changed && !!account?.first_name?.trim() && !badPhone && !saving;

    const saveAccount = () => {
        setSaving(true);
        accountServices.saveAccount({
            first_name: account.first_name.trim(),
            last_name: (account.last_name || "").trim(),
            phone_primary: account.phone_primary || ""
        }).then((data) => {
            setSaved(data);
            setAccount(data);
            // El menú muestra el nombre del usuario de la sesión.
            setAuthData({ authUser: { ...authUser, first_name: data.first_name, last_name: data.last_name } });
            if (phoneChanged && data.whatsapp_phone && !data.whatsapp_verified) setVerifyingPhone(true);
            else notify("Tus datos quedaron guardados");
        }).catch(() => {}).finally(() => setSaving(false));
    };

    // Solo cambia el estado de verificación: lo que se esté editando en el formulario queda como está.
    const phoneVerified = (data) => {
        const verification = { whatsapp_phone: data.whatsapp_phone, whatsapp_verified: data.whatsapp_verified };
        setSaved((current) => ({ ...current, ...verification }));
        setAccount((current) => ({ ...current, ...verification }));
        setVerifyingPhone(false);
        notify("Tu WhatsApp quedó verificado");
    };

    const shortPassword = !!passwords.password && passwords.password.length < MIN_PASSWORD_LENGTH;
    const mismatch = !!passwords.password_confirmation && passwords.password !== passwords.password_confirmation;
    const canChangePassword = !!passwords.current_password && !!passwords.password && !shortPassword
        && passwords.password === passwords.password_confirmation && !changingPassword;

    const changePassword = () => {
        setChangingPassword(true);
        accountServices.changePassword(passwords).then(() => {
            setPasswords(EMPTY_PASSWORDS);
            notify("Tu contraseña cambió");
        }).catch(() => {}).finally(() => setChangingPassword(false));
    };

    const setPassword = (field) => (event) => setPasswords((current) => ({ ...current, [field]: event.target.value }));

    return (
        <Box>
            <StandardPageHeader title="Mi perfil" subtitle="Tus datos de contacto y tu contraseña. El correo y los perfiles los cambia un administrador de la empresa." />
            {!account ? (
                <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress /></Box>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12} lg={7}>
                        <SectionCard title="Datos personales" subtitle="Tu teléfono sirve para recibir alertas por WhatsApp. Verificado, también para preguntarle a Xpit Assistant por WhatsApp.">
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Nombre"
                                        value={account.first_name || ""}
                                        error={!account.first_name?.trim()}
                                        helperText={!account.first_name?.trim() ? "Escribe tu nombre" : " "}
                                        onChange={(event) => setAccount({ ...account, first_name: event.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Apellido"
                                        value={account.last_name || ""}
                                        helperText=" "
                                        onChange={(event) => setAccount({ ...account, last_name: event.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        disabled
                                        label="Correo electrónico"
                                        value={account.email || ""}
                                        helperText="Lo cambia un administrador de la empresa."
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <PhoneField
                                        fullWidth
                                        label="Teléfono"
                                        value={account.phone_primary || ""}
                                        onChange={(phone) => setAccount((current) => ({ ...current, phone_primary: phone }))}
                                        onStatusChange={setPhoneStatus}
                                    />
                                    {!!saved.whatsapp_phone && !phoneChanged && (
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                            {saved.whatsapp_verified ? (
                                                <Chip size="small" color="success" variant="outlined" icon={<VerifiedOutlinedIcon />} label="WhatsApp verificado" />
                                            ) : (
                                                <>
                                                    <Chip size="small" color="warning" variant="outlined" label="WhatsApp sin verificar" />
                                                    <Button size="small" color="success" startIcon={<WhatsAppIcon />} onClick={() => setVerifyingPhone(true)}>
                                                        Verificar
                                                    </Button>
                                                </>
                                            )}
                                        </Stack>
                                    )}
                                </Grid>
                            </Grid>
                            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                                <SaveButton loading={saving} disabled={!canSave} onClick={saveAccount}>Guardar cambios</SaveButton>
                            </Stack>
                        </SectionCard>
                    </Grid>
                    <Grid item xs={12} lg={5}>
                        <SectionCard title="Contraseña" subtitle="Para cambiarla necesitas la actual. Tu sesión sigue abierta.">
                            <Stack spacing={2}>
                                <TextField
                                    fullWidth
                                    type="password"
                                    autoComplete="current-password"
                                    label="Contraseña actual"
                                    value={passwords.current_password}
                                    onChange={setPassword("current_password")}
                                />
                                <TextField
                                    fullWidth
                                    type="password"
                                    autoComplete="new-password"
                                    label="Contraseña nueva"
                                    value={passwords.password}
                                    error={shortPassword}
                                    helperText={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres.`}
                                    onChange={setPassword("password")}
                                />
                                <TextField
                                    fullWidth
                                    type="password"
                                    autoComplete="new-password"
                                    label="Repite la contraseña nueva"
                                    value={passwords.password_confirmation}
                                    error={mismatch}
                                    helperText={mismatch ? "No coincide con la contraseña nueva." : " "}
                                    onChange={setPassword("password_confirmation")}
                                />
                            </Stack>
                            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                                <SaveButton loading={changingPassword} disabled={!canChangePassword} startIcon={<LockOutlinedIcon />} onClick={changePassword}>
                                    Cambiar contraseña
                                </SaveButton>
                            </Stack>
                        </SectionCard>
                    </Grid>
                    <Grid item xs={12}>
                        <ApiAccessSection />
                    </Grid>
                </Grid>
            )}
            <WhatsappVerificationDialog
                open={verifyingPhone}
                phone={saved?.whatsapp_phone}
                onClose={() => setVerifyingPhone(false)}
                onVerified={phoneVerified}
            />
        </Box>
    );
};

export default MyProfile;
